import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';
import SeekerGuard from '../components/SeekerGuard';

const STATUS_COLOR = {
  applied:   { bg:'#f0f9ff', color:'#0369a1', label:'Applied' },
  reviewed:  { bg:'#f0fdf4', color:'#166534', label:'Under Review' },
  interview: { bg:'#faf5ff', color:'#6d28d9', label:'Interview' },
  offered:   { bg:'#f0fdf4', color:'#166534', label:'Offered' },
  rejected:  { bg:'#fef2f2', color:'#991b1b', label:'Not Selected' },
  withdrawn: { bg:'#f8fafc', color:'#64748b', label:'Withdrawn' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg:'#f8fafc', color:'#64748b', label: status };
  return (
    <span style={{ background:s.bg, color:s.color, padding:'3px 10px', borderRadius:999, fontSize:11, fontWeight:700 }}>
      {s.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

function DashboardContent({ auth }) {
  const { user, token, logout } = auth;
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    jbApi.seekerApplications(token)
      .then(d => setApplications(d.applications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const totalApps   = applications.length;
  const interviews  = applications.filter(a => a.status === 'interview').length;
  const offers      = applications.filter(a => a.status === 'offered').length;
  const fullName    = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Seeker';
  const profileComplete = !!(user?.resume_url && user?.headline && user?.location);

  return (
    <div className="jb-page" data-testid="seeker-dashboard">
      {/* Portal Header */}
      <div style={{ background:'#0D2137', padding:'16px 0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:11, color:'rgba(245,240,232,.6)', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', marginBottom:2 }}>Job Seeker Portal</div>
            <div style={{ fontSize:16, fontWeight:700, color:'#F5F0E8' }}>{fullName}</div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <Link to="/career-hub" style={{ background:'#C9A84C', color:'#0D2137', padding:'8px 18px', borderRadius:8, fontSize:13, fontWeight:800, textDecoration:'none' }} data-testid="browse-jobs-btn">
              Browse Jobs
            </Link>
            <button onClick={logout} style={{ background:'rgba(255,255,255,.1)', border:'none', color:'rgba(245,240,232,.7)', padding:'8px 14px', borderRadius:8, fontSize:12, cursor:'pointer', fontFamily:'Inter,sans-serif' }} data-testid="seeker-logout-btn">
              Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Nav Tabs */}
      <div style={{ background:'#fff', borderBottom:'1px solid #ddd8cc' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px', display:'flex', gap:4 }}>
          {[
            { label:'Dashboard', to:'/seeker/dashboard' },
            { label:'My Profile', to:'/seeker/profile' },
          ].map(({ label, to }) => (
            <Link key={to} to={to} style={{
              padding:'14px 16px', fontSize:13, fontWeight:600,
              color: window.location.pathname === to ? '#1B6B72' : '#5a6373',
              borderBottom: window.location.pathname === to ? '2px solid #1B6B72' : '2px solid transparent',
              textDecoration:'none', whiteSpace:'nowrap',
            }}>{label}</Link>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'28px 24px 60px' }}>
        {/* Profile completion nudge */}
        {!profileComplete && (
          <div style={{ background:'rgba(201,168,76,.1)', border:'1px solid rgba(201,168,76,.35)', borderRadius:10, padding:'12px 18px', marginBottom:22, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }} data-testid="profile-nudge">
            <div style={{ fontSize:13, color:'#0D2137', fontWeight:500 }}>
              Complete your profile to stand out — add your resume, headline, and location.
            </div>
            <Link to="/seeker/profile" style={{ fontSize:12, fontWeight:700, color:'#C9A84C', textDecoration:'none', whiteSpace:'nowrap' }}>Complete Profile →</Link>
          </div>
        )}

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))', gap:16, marginBottom:28 }} data-testid="seeker-stats">
          {[
            { label:'Applications Sent', value: totalApps, color:'#0D2137' },
            { label:'Interviews',         value: interviews, color:'#6d28d9' },
            { label:'Offers',             value: offers,     color:'#166534' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background:'#fff', borderRadius:10, padding:'18px 20px', boxShadow:'0 2px 8px rgba(13,33,55,.07)' }}>
              <div style={{ fontSize:28, fontWeight:800, color, fontFamily:'Inter,sans-serif', marginBottom:4 }}>{value}</div>
              <div style={{ fontSize:12, color:'#5a6373', fontWeight:500 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Applications list */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:'#0D2137' }}>My Applications</h2>
        </div>

        {loading && <div style={{ textAlign:'center', padding:60, color:'#5a6373' }}>Loading applications…</div>}

        {!loading && applications.length === 0 && (
          <div style={{ background:'#fff', borderRadius:10, padding:'40px 20px', textAlign:'center', boxShadow:'0 2px 8px rgba(13,33,55,.07)' }}>
            <div style={{ fontSize:13, color:'#5a6373', marginBottom:14 }}>You haven't applied to any jobs yet.</div>
            <Link to="/career-hub" style={{ background:'#1B6B72', color:'#fff', padding:'10px 20px', borderRadius:8, fontSize:13, fontWeight:700, textDecoration:'none' }}>Browse Opportunities →</Link>
          </div>
        )}

        {applications.map(app => {
          const jobTitle = app.job?.title || 'Position';
          const org      = app.job?.organization || 'Company';
          return (
          <div key={app.id} data-testid={`application-${app.id}`} style={{ background:'#fff', borderRadius:10, padding:'14px 18px', marginBottom:10, boxShadow:'0 2px 8px rgba(13,33,55,.07)', display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:'#1B6B72', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0 }}>
              {jobTitle.slice(0,2).toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#0D2137', marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {jobTitle}
              </div>
              <div style={{ fontSize:12, color:'#5a6373' }}>
                {org} · Applied {fmtDate(app.created_at)}
              </div>
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexShrink:0 }}>
              <StatusBadge status={app.status} />
              <Link to={`/career-hub/${app.job_id}`} style={{ fontSize:12, color:'#1B6B72', fontWeight:600, textDecoration:'none', whiteSpace:'nowrap' }}>
                View Job →
              </Link>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SeekerDashboardPage() {
  const auth = useJBAuth();
  return <SeekerGuard><DashboardContent auth={auth} /></SeekerGuard>;
}
